/**
 * The six artifacts of the "Explain an idea" demo: a team lead asks the other leads to replace the
 * 40 person Monday status meeting with a short written update from every team, posted by noon.
 *
 * One idea per artifact, carried by something people already know how to read: the cancelled
 * calendar event, the hours it costs as a grid, the written update itself, two weeks side by side,
 * the pushback as a chat, and the trial as a plan.
 */

import { SANS, SERIF, circle, esc, line, lines, path, rect, text, wrap, type DemoSlide, type LinesOpts } from './slideSvg';

const KIT = {
  cream: '#F3EFE7',
  ink: '#161616',
  muted: '#7A766E',
  lilac: '#D8CCF3',
  violet: '#5B4B8A',
  lime: '#D9FF3F',
  white: '#FFFFFF',
  /** Hairlines inside white cards. */
  hair: '#ECE7DF',
  /** The other side of a chat. */
  grey: '#E6E1D8',
  /** Focus time: lilac thinned on white. */
  mist: '#EEE8FB',
} as const;

/** The grid every content artifact shares. */
const M = 64;
const RIGHT = 1280 - M;
const HEAD_Y = 160;
const HEAD_LEAD = 68;
/** Top of the headline's capitals: where a visual beside it starts. */
const TOP = 114;
/** The lowest a visual reaches. */
const BOTTOM = 656;
/** Left edge of a visual that sits beside the headline. */
const SIDE_X = 560;
/** Top of a full-width visual under a one line headline. */
const WIDE_TOP = 232;

/** Inter Regular advances for printable ASCII in thousandths of an em, measured in Chrome. */
const ADVANCE = (
  '281 288 466 633 642 982 644 300 365 365 501 662 288 460 288 360 631 407 610 618 646 593 620 566 619 620 288 302 662 662 662 511 ' +
  '966 690 654 730 722 601 590 746 743 269 571 672 565 903 753 765 639 765 644 642 646 744 690 985 682 679 629 365 360 365 471 456 ' +
  '323 562 612 571 612 583 370 613 591 242 242 549 242 876 591 600 612 612 376 528 327 591 562 818 546 562 552 426 333 426 662'
)
  .split(' ')
  .map(Number);
const ADVANCE_EXTRA: Record<string, number> = { '’': 261, '·': 288, '×': 662 };

/** The width of one line of Inter with no tracking. Medium and SemiBold run a little wider. */
function measure(str: string, size: number, weight = 400): number {
  let units = 0;
  for (const ch of str) {
    const code = ch.charCodeAt(0);
    units += code >= 32 && code < 127 ? ADVANCE[code - 32] : ADVANCE_EXTRA[ch] ?? 600;
  }
  return (units / 1000) * size * (weight >= 600 ? 1.02 : weight >= 500 ? 1.01 : 1);
}

/** Interface text: Inter with no tracking, so `measure` holds. */
const ui = (x: number, y: number, size: number, str: string, opts: LinesOpts = {}) =>
  text(x, y, size, str, { fill: KIT.ink, tracking: '0', ...opts });

/** The quiet running line on every content artifact. */
const running = (page: number, fill: string) =>
  ui(M, 56, 13, 'No more Monday meeting', { fill }) + ui(RIGHT, 56, 13, String(page).padStart(2, '0'), { fill, anchor: 'end' });

const headline = (rows: string[], fill: string) =>
  lines(M, HEAD_Y, 64, HEAD_LEAD, rows, { font: SERIF, fill, tracking: '-0.01em' });

/** The one supporting line, under a headline of `rows` lines. */
const support = (str: string, rows: number, fill: string) => ui(M, HEAD_Y + (rows - 1) * HEAD_LEAD + 52, 20, str, { fill });

/** A 24 unit line icon drawn at `size`, top left at x, y. */
function icon(d: string, x: number, y: number, size: number, stroke: string, width = 2): string {
  const k = size / 24;
  return `<path d="${d}" transform="translate(${x} ${y}) scale(${k})" fill="none" stroke="${stroke}" stroke-width="${width / k}" stroke-linecap="round" stroke-linejoin="round"/>`;
}

const ICON = {
  clock: 'M21 12a9 9 0 1 1-18 0a9 9 0 0 1 18 0Z M12 7v5l3 2',
  repeat: 'M17 2l4 4-4 4 M3 11v-1a4 4 0 0 1 4-4h14 M7 22l-4-4 4-4 M21 13v1a4 4 0 0 1-4 4H3',
  check: 'M20 6L9 17l-5-5',
  heart: 'M19 14c1.5-1.5 3-3.2 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.8 0-3 .5-4.5 2-1.5-1.5-2.7-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4 3 5.5l7 7Z',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z M15 12a3 3 0 1 1-6 0a3 3 0 0 1 6 0Z',
  card: 'M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z M2 10h20 M6 15h4',
  pen: 'M12 20h9 M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z',
} as const;

/** A rounded box whose corners can differ: top left, top right, bottom right, bottom left. */
function box(x: number, y: number, w: number, h: number, [tl, tr, br, bl]: number[], fill: string): string {
  return path(
    `M${x + tl} ${y}H${x + w - tr}A${tr} ${tr} 0 0 1 ${x + w} ${y + tr}V${y + h - br}A${br} ${br} 0 0 1 ${x + w - br} ${y + h}` +
      `H${x + bl}A${bl} ${bl} 0 0 1 ${x} ${y + h - bl}V${y + tl}A${tl} ${tl} 0 0 1 ${x + tl} ${y}Z`,
    fill,
  );
}

/** A rectangle outline, optionally dashed. */
const outline = (x: number, y: number, w: number, h: number, stroke: string, r: number, dash = '') =>
  `<rect x="${x + 0.75}" y="${y + 0.75}" width="${w - 1.5}" height="${h - 1.5}" rx="${r}" fill="none" stroke="${stroke}" stroke-width="1.5"${dash ? ` stroke-dasharray="${dash}"` : ''}/>`;

/* 1 · Cover: the calendar event, cancelled. */

function avatar(cx: number, cy: number, fill: string, initials: string, ink: string): string {
  return circle(cx, cy, 16, KIT.white) + circle(cx, cy, 14, fill) + ui(cx, cy + 4.5, 12, initials, { fill: ink, weight: 600, anchor: 'middle' });
}

function cancelledEvent(x: number, y: number): string {
  const w = RIGHT - x;
  const title = 'Monday status';
  const titleW = measure(title, 28, 500);
  const tag = 'Cancelled';
  const tagW = measure(tag, 13, 500) + 24;
  const time = '10:00 to 11:00';
  const repeatX = x + 62 + measure(time, 17) + 18;
  return (
    rect(x, y, w, 200, KIT.white, 20) +
    rect(x + 32, y + 42, 14, 14, KIT.violet, 4) +
    ui(x + 62, y + 59, 28, title, { weight: 500 }) +
    line(x + 58, y + 50, x + 66 + titleW, y + 50, KIT.ink, 2) +
    rect(x + w - 28 - tagW, y + 35, tagW, 28, KIT.lime, 14) +
    ui(x + w - 28 - tagW / 2, y + 53.5, 13, tag, { weight: 500, anchor: 'middle' }) +
    icon(ICON.clock, x + 30, y + 90, 18, KIT.muted, 1.75) +
    ui(x + 62, y + 105, 17, time, { fill: KIT.muted }) +
    icon(ICON.repeat, repeatX, y + 91, 16, KIT.muted, 1.75) +
    ui(repeatX + 24, y + 105, 17, 'Every Monday', { fill: KIT.muted }) +
    avatar(x + 47, y + 148, KIT.violet, 'NK', KIT.white) +
    avatar(x + 73, y + 148, KIT.lilac, 'AB', KIT.violet) +
    avatar(x + 99, y + 148, KIT.ink, 'LT', KIT.white) +
    avatar(x + 125, y + 148, KIT.cream, 'GS', KIT.ink) +
    ui(x + 155, y + 154, 17, '40 guests', { fill: KIT.muted })
  );
}

const cover = wrap(
  rect(0, 0, 1280, 720, KIT.lilac) +
  ui(M, 56, 15, 'A proposal for the leads · Thursday', { fill: KIT.violet }) +
  lines(M, 334, 100, 96, ['No more', 'Monday meeting'], { font: SERIF, fill: KIT.violet, tracking: '-0.02em' }) +
  cancelledEvent(744, 258),
);

/* 2 · What it costs: every square is one person for one hour. */

/** 52 Mondays across, 40 people down: one dashed stroke per person, one dash per Monday. */
function hoursGrid(right: number, top: number): { svg: string; x: number; h: number } {
  const cell = 10.2;
  const gap = 2.4;
  const w = 52 * (cell + gap) - gap;
  const h = 40 * (cell + gap) - gap;
  const x = right - w;
  const rows = Array.from({ length: 40 }, (_, r) => `M${x} ${top + r * (cell + gap) + cell / 2}H${right}`).join('');
  return {
    svg: `<path d="${rows}" fill="none" stroke="${KIT.lilac}" stroke-width="${cell}" stroke-dasharray="${cell} ${gap}"/>`,
    x,
    h,
  };
}

const grid = hoursGrid(RIGHT, TOP);
const cost = wrap(
  rect(0, 0, 1280, 720, KIT.cream) +
  running(2, KIT.muted) +
  headline(['2,080 hours', 'a year.'], KIT.ink) +
  support('The same as one full-time job.', 2, KIT.muted) +
  grid.svg +
  ui(grid.x, TOP + grid.h + 34, 14, '52 Mondays × 40 people', { fill: KIT.muted }) +
  rect(RIGHT - measure('One person-hour', 14) - 20, TOP + grid.h + 23, 10, 10, KIT.lilac) +
  ui(RIGHT, TOP + grid.h + 34, 14, 'One person-hour', { fill: KIT.muted, anchor: 'end' }),
);

/* 3 · The idea: the written update, as it lands in the team chat. */

/** One line of an update: the label in Medium, the rest in Regular. */
const updateLine = (x: number, y: number, label: string, value: string) =>
  `<text x="${x}" y="${y}" font-family="${SANS}" font-size="22" fill="${KIT.ink}" letter-spacing="0"><tspan font-weight="500">${esc(label)}</tspan> ${esc(value)}</text>`;

function reaction(x: number, y: number, d: string, count: string): { svg: string; w: number } {
  const w = 14 + 16 + 8 + measure(count, 15, 500) + 14;
  return {
    svg:
      rect(x, y, w, 34, KIT.cream, 17) +
      outline(x, y, w, 34, KIT.hair, 16.25) +
      icon(d, x + 14, y + 9, 16, KIT.violet, 1.75) +
      ui(x + 38, y + 22.5, 15, count, { weight: 500 }),
    w,
  };
}

function updateCard(x: number, y: number): string {
  const w = RIGHT - x;
  const h = 448;
  const tx = x + 104;
  const rows: [string, string][] = [
    ['Shipped:', 'refunds in one click'],
    ['Next:', 'invoices in any currency'],
    ['Blocked:', 'nothing'],
    ['Needs:', 'one review from Legal'],
    ['Thanks:', 'Support, for testing refunds'],
  ];
  let out =
    rect(x, y, w, h, KIT.white, 20) +
    ui(x + 32, y + 47, 15, '# monday-updates', { fill: KIT.muted, weight: 500 }) +
    rect(x, y + 74, w, 1, KIT.hair) +
    rect(x + 32, y + 104, 52, 52, KIT.violet, 13) +
    icon(ICON.card, x + 45, y + 117, 26, KIT.white, 1.75) +
    ui(tx, y + 124, 19, 'Payments', { weight: 600 }) +
    ui(tx + measure('Payments', 19, 600) + 10, y + 124, 15, 'Monday 11:52', { fill: KIT.muted });
  rows.forEach(([label, value], i) => {
    out += updateLine(tx, y + 162 + i * 44, label, value);
  });
  let rx = tx;
  for (const [d, count] of [[ICON.check, '14'], [ICON.heart, '6'], [ICON.eye, '21']] as const) {
    const r = reaction(rx, y + h - 70, d, count);
    out += r.svg;
    rx += r.w + 8;
  }
  return out + ui(rx + 10, y + h - 47.5, 15, '3 replies', { fill: KIT.violet, weight: 500 });
}

const idea = wrap(
  rect(0, 0, 1280, 720, KIT.lilac) +
  running(3, KIT.violet) +
  headline(['Write it.', 'Don’t say it.'], KIT.violet) +
  support('Every team posts five lines by Monday noon.', 2, KIT.violet) +
  updateCard(SIDE_X, TOP),
);

/* 4 · How a week looks: the same week, before and after. */

interface CalEvent {
  day: number;
  start: number;
  end: number;
  title: string[];
  kind: 'other' | 'meeting' | 'focus';
}

const OTHER_EVENTS: CalEvent[] = [
  { day: 1, start: 9, end: 10, title: ['1:1'], kind: 'other' },
  { day: 2, start: 11, end: 12, title: ['Customer', 'call'], kind: 'other' },
  { day: 3, start: 9.5, end: 11, title: ['Planning'], kind: 'other' },
  { day: 4, start: 12, end: 13, title: ['Team lunch'], kind: 'other' },
];

/** Two calendars side by side with a 24px gutter, showing 9:00 to 13:00. */
const CAL_W = (RIGHT - M - 24) / 2;
const FIRST_HOUR = 9;
const LAST_HOUR = 13;

function weekCalendar(x: number, label: string, events: CalEvent[], posted: boolean): string {
  const y = WIDE_TOP;
  const w = CAL_W;
  const gx = x + 62;
  const colW = (x + w - 8 - gx) / 5;
  const gy = y + 100;
  const hourH = 76;
  const hourY = (t: number) => gy + (t - FIRST_HOUR) * hourH;
  let out = rect(x, y, w, BOTTOM - y, KIT.white, 20) + ui(x + 24, y + 42, 16, label, { weight: 500 });
  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].forEach((d, i) => {
    out += ui(gx + i * colW + 8, y + 82, 13, d, { fill: KIT.muted });
    if (i > 0) out += line(gx + i * colW, gy, gx + i * colW, hourY(LAST_HOUR), KIT.hair);
  });
  for (let t = FIRST_HOUR; t <= LAST_HOUR; t += 1) {
    out += line(gx, hourY(t), x + w - 8, hourY(t), KIT.hair);
    out += ui(gx - 11, hourY(t) + 4, 12, `${t}:00`, { fill: KIT.muted, anchor: 'end' });
  }
  for (const e of events) {
    const ex = gx + e.day * colW + 3;
    const ey = hourY(e.start) + 3;
    const fill = e.kind === 'meeting' ? KIT.violet : e.kind === 'focus' ? 'url(#idea-focus)' : KIT.cream;
    const ink = e.kind === 'meeting' ? KIT.white : e.kind === 'focus' ? KIT.violet : KIT.ink;
    out += rect(ex, ey, colW - 6, (e.end - e.start) * hourH - 6, fill, 8) + lines(ex + 9, ey + 19, 12, 15, e.title, { fill: ink, weight: 500, tracking: '0' });
  }
  if (posted) {
    const py = hourY(12);
    out += line(gx, py, gx + colW, py, KIT.violet, 2) + circle(gx, py, 4.5, KIT.violet) + ui(gx + 3, py + 18, 12, 'Updates posted', { fill: KIT.violet, weight: 500 });
  }
  return out;
}

const focusHatch =
  '<defs><pattern id="idea-focus" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">' +
  `<rect width="7" height="7" fill="${KIT.mist}"/><rect width="2.5" height="7" fill="${KIT.lilac}"/></pattern></defs>`;

const week = wrap(
  focusHatch +
  rect(0, 0, 1280, 720, KIT.cream) +
  running(4, KIT.muted) +
  headline(['Same news. One less meeting.'], KIT.ink) +
  weekCalendar(M, 'Before', [{ day: 0, start: 10, end: 11, title: ['Status', 'meeting'], kind: 'meeting' }, ...OTHER_EVENTS], false) +
  weekCalendar(RIGHT - CAL_W, 'After', [{ day: 0, start: 10, end: 11, title: ['Focus time'], kind: 'focus' }, ...OTHER_EVENTS], true),
);

/* 5 · What you will ask: three questions from the leads, answered in the thread. */

function bubble(x: number, y: number, str: string, side: 'left' | 'right'): string {
  const size = 21;
  const w = measure(str, size) + 44;
  const left = side === 'left' ? x : x - w;
  const corners = side === 'left' ? [22, 22, 22, 6] : [22, 22, 6, 22];
  const fill = side === 'left' ? KIT.grey : KIT.lilac;
  return box(left, y, w, 56, corners, fill) + ui(left + 22, y + 35.5, size, str, { fill: side === 'left' ? KIT.ink : KIT.violet });
}

const EXCHANGES: [string, string, string][] = [
  ['Head of Design', 'Do we lose face time?', 'Keep Friday lunch.'],
  ['Head of Sales', 'Will anyone read it?', 'Five lines. Faster than the meeting.'],
  ['Head of Engineering', 'Won’t problems hide?', 'Blocked items are flagged red.'],
];

const questions = wrap(
  rect(0, 0, 1280, 720, KIT.cream) +
  running(5, KIT.muted) +
  headline(['Three questions,', 'answered.'], KIT.ink) +
  EXCHANGES.map(([who, q, a], i) => {
    const y = TOP + i * 178;
    return ui(SIDE_X + 4, y + 13, 13, who, { fill: KIT.muted }) + bubble(SIDE_X, y + 24, q, 'left') + bubble(RIGHT, y + 90, a, 'right');
  }).join(''),
);

/* 6 · The ask: four weeks, a review, and the way back. */

/** The trial as a plan: one track for the written updates, one for the meeting they replace. */
function trialPlan(y: number): string {
  const w = RIGHT - M;
  const h = 280;
  const ax = M + 28;
  const colW = (w - 56) / 5;
  const weeks = [['Week 1', 'Oct 5'], ['Week 2', 'Oct 12'], ['Week 3', 'Oct 19'], ['Week 4', 'Oct 26'], ['Week 5', 'Nov 2']];
  const headerY = y + 76;
  let out = rect(M, y, w, h, KIT.white, 20) + line(M, headerY, RIGHT, headerY, KIT.hair);
  weeks.forEach(([week, date], i) => {
    const cx = ax + i * colW;
    out += ui(cx + 16, y + 46, 15, week, { weight: 500, fill: i === 4 ? KIT.muted : KIT.ink });
    out += ui(cx + 16 + measure(week, 15, 500) + 8, y + 46, 13, date, { fill: KIT.muted });
    if (i > 0) out += line(cx, headerY, cx, y + h - 24, KIT.hair);
  });
  const reviewX = ax + 4 * colW;
  const trackW = reviewX - 26 - (ax + 10);
  const updatesY = y + 108;
  out += rect(ax + 10, updatesY, trackW, 56, KIT.lilac, 12) + icon(ICON.pen, ax + 30, updatesY + 18, 20, KIT.violet, 1.75);
  out += ui(ax + 62, updatesY + 34, 17, 'Written updates, every Monday by noon', { fill: KIT.violet, weight: 500 });
  out += `<rect x="${reviewX - 11}" y="${updatesY + 17}" width="22" height="22" rx="4" transform="rotate(45 ${reviewX} ${updatesY + 28})" fill="${KIT.lime}" stroke="${KIT.ink}" stroke-width="1.5"/>`;
  out += ui(reviewX + 26, updatesY + 34, 17, 'Review', { weight: 500 });
  const meetingY = updatesY + 76;
  out += rect(ax + 10, meetingY, trackW, 56, KIT.cream, 12) + ui(ax + 30, meetingY + 34, 17, 'Monday meeting, paused', { fill: KIT.muted });
  out += outline(reviewX + 10, meetingY, colW - 20, 56, KIT.muted, 12, '5 5') + ui(reviewX + 28, meetingY + 34, 17, 'Back if needed', { fill: KIT.muted });
  return out;
}

const ask = wrap(
  rect(0, 0, 1280, 720, KIT.lilac) +
  running(6, KIT.violet) +
  headline(['Try it for four weeks.'], KIT.violet) +
  trialPlan(WIDE_TOP) +
  ui(M, 572, 20, 'If it does not work, the meeting is back in week five.', { fill: KIT.violet }),
);

export const IDEA_SLIDES: DemoSlide[] = [
  { id: 'idea-1', title: 'Cover', svg: cover },
  { id: 'idea-2', title: 'What it costs', svg: cost },
  { id: 'idea-3', title: 'The idea', svg: idea },
  { id: 'idea-4', title: 'How a week looks', svg: week },
  { id: 'idea-5', title: 'What you will ask', svg: questions },
  { id: 'idea-6', title: 'The ask', svg: ask },
];
